import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditCategoryModal from 'components/admin/common/modal/product/EditCategoryModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const Category = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createCategoryData,
        postError: creatingError,
        postLoading: creating,
        putData: updateCategoryData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteCategoryData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.CATEGORIES);

    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteCategory = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditCategory = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteCategory, modalData: categoryToDelete, openModal: openDeleteCategory, closeModal: closedDeleteCategory } = useModalState({
        onClose: handleCloseDeleteCategory
    });

    const { isOpen: isOpenEditCategory, modalData: selectedCategory, openModal: openEditCategory, closeModal: closedEditCategory } = useModalState({
        onClose: handleCloseEditCategory
    });

    const validationRules = [
        { field: 'title', method: 'required' },
    ];

    const categoryOptions = useMemo(() => {
        if (data?.length > 0) {
            return data.map(categorie => ({
                label: categorie.title,
                value: categorie.id
            }));
        }
    }, [data]);

    const handleModalSave = useCallback(async (updatedCategory) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedCategory, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedCategory.id) {
                await updateCategoryData(updatedCategory.id, updatedCategory, true);
                setSuccessMessage("Categoría actualizada con éxito.");
            } else {
                await createCategoryData(updatedCategory, true);
                setSuccessMessage("Categoría creada con éxito.");
            }
            closedEditCategory();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateCategoryData, createCategoryData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newCategory = {
            title: '',
            parent_id: ''
        };
        openEditCategory(newCategory);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (categoryToDelete) {
            try {
                await deleteCategoryData(categoryToDelete.id);
                setSuccessMessage("Categoría eliminada con éxito.");
                refetch();
                closedDeleteCategory();
            } catch (error) {
                console.error(error);
            }
        }
    }, [categoryToDelete, deleteCategoryData, refetch]);

    const columns = useMemo(() => [
        { field: 'title', header: 'Título', sortable: true },
        { field: 'parent_id', header: 'ID del Padre', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Categorías</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Categoría
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditCategory}
                onDelete={openDeleteCategory}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditCategoryModal
                show={isOpenEditCategory}
                category={selectedCategory}
                handleClose={closedEditCategory}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
                categoryOptions={categoryOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteCategory}
                onHide={closedDeleteCategory}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Category);